/**
 * Hub pages as code (wayai-pro/wayai#5333): `pages/<slug>/page.yaml` (the manifest) and
 * `pages/<slug>/index.html` (the page, one self-contained file) in a hub folder.
 *
 * ── THE `pages/` FOLDER IS THE DESIRED SET, WHEN IT EXISTS ──
 * A hub folder holding a `pages/` folder sends `pages` on push, diff and pull's comparison: every
 * page folder in it, `[]` when it holds none, so removing a page's folder deletes that page. A
 * hub folder without one sends no `pages` key, which the platform reads as "leave the pages as
 * they are": a folder an older CLI wrote never deletes a page. Pull writes the folder whenever
 * the hub holds a page, and removes it once the hub holds none.
 *
 * ── A PAGE FOLDER IS WHOLE OR REFUSED ──
 * A page folder is the page's desired state, so one the CLI cannot read whole — no `index.html`
 * beside its `page.yaml`, or the reverse, a manifest that is not a YAML object, a `slug` that is
 * not the folder's name, HTML that is empty or past the cap, a symlink — stops the command rather than being
 * skipped, which would delete the page. A folder holding neither file is not a page and is left
 * alone, as are stray files in `pages/`.
 *
 * Pull writes only `page.yaml` and `index.html`, and removes only those two from the folder of a
 * page the hub no longer holds (then the folder, if that leaves it empty): a builder's other files
 * beside a page (sources, notes) are never touched (`writePageFiles`, `yaml-writer.ts`).
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { pageHtmlIssue } from '@wayai/core/contracts/pages';
import type { HubAsCodePage } from '@wayai/core/contracts/ci';
import { expected } from './expected.js';
import { readRealFileOrThrow, symlinkRefusal } from './fs-safety.js';

export const PAGES_DIR = 'pages';
export const PAGE_MANIFEST_FILE = 'page.yaml';
export const PAGE_HTML_FILE = 'index.html';

/**
 * The pages of `hubFolder`'s `pages/` folder, in folder order, or undefined when there is none.
 * Throws (`expected`) on a page folder it cannot read whole (see the header).
 */
export function readPages(hubFolder: string): HubAsCodePage[] | undefined {
  const dir = path.join(hubFolder, PAGES_DIR);
  let st: fs.Stats;
  try {
    st = fs.lstatSync(dir);
  } catch {
    return undefined;
  }
  if (!st.isDirectory()) throw symlinkRefusal(path.join(path.basename(hubFolder), PAGES_DIR), 'directory');

  const pages: HubAsCodePage[] = [];
  const entries = fs.readdirSync(dir, { withFileTypes: true }).sort((a, b) => a.name.localeCompare(b.name));
  for (const entry of entries) {
    const rel = `${PAGES_DIR}/${entry.name}`;
    if (entry.isSymbolicLink()) throw symlinkRefusal(rel, 'file or folder');
    if (!entry.isDirectory()) continue;
    const manifestText = readRealFileOrThrow(hubFolder, path.join(dir, entry.name, PAGE_MANIFEST_FILE));
    const html = readRealFileOrThrow(hubFolder, path.join(dir, entry.name, PAGE_HTML_FILE));
    if (manifestText === null && html === null) continue;
    if (manifestText === null) throw expected(`${rel}/${PAGE_HTML_FILE} has no ${PAGE_MANIFEST_FILE} beside it: a page is both files.`);
    if (html === null) throw expected(`${rel}/${PAGE_MANIFEST_FILE} has no ${PAGE_HTML_FILE} beside it: a page is both files.`);

    let manifest: unknown;
    try {
      manifest = yaml.load(manifestText);
    } catch (err) {
      if (err instanceof yaml.YAMLException) throw expected(`Invalid YAML in ${rel}/${PAGE_MANIFEST_FILE}: ${err.message}`);
      throw err;
    }
    if (!manifest || typeof manifest !== 'object' || Array.isArray(manifest)) {
      throw expected(`${rel}/${PAGE_MANIFEST_FILE} is not a YAML object.`);
    }
    const stated = (manifest as Record<string, unknown>).slug;
    if (stated !== undefined && stated !== entry.name) {
      throw expected(`${rel}/${PAGE_MANIFEST_FILE} names slug "${String(stated)}": a page's folder is named by its slug. Rename the folder or the slug (a new slug is a new page).`);
    }
    const htmlIssue = pageHtmlIssue(html);
    if (htmlIssue) throw expected(`${rel}/${PAGE_HTML_FILE}: ${htmlIssue}.`);
    pages.push({ manifest: { slug: entry.name, ...(manifest as Record<string, unknown>) }, html });
  }
  return pages;
}
